import { useMemo, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { addDays, differenceInYears } from 'date-fns';
import { SCHEDULE_LIMIT_MONTHS } from '@/features/Vacations/vacations.definitions';
import { VacationPeriod } from '@/features/Collaborators/Edit/Types';
import { addUtcMonths, resolveEnjoymentStartDate } from '../vacationEnjoymentStart';
import {
    countVacationDays,
    hasStarted,
    isLegacyPeriod,
    sumScheduledDays,
    sumStartedDays,
} from '../vacationPeriodTotals';

interface VacationHistoryEntry {
    date: string;
    description: string;
    credit?: number;
    debit?: number;
}

interface NextCompulsoryPeriod {
    days: number;
    date: string | null;
}

interface VacationCalculationResult {
    balance: number;
    expiredCompulsoryDays: number;
    nextCompulsoryPeriod: NextCompulsoryPeriod;
    scheduledDays: number;
    vacationHistory: VacationHistoryEntry[];
    handleAddVacation: () => void;
    handleRemoveVacation: (index: number) => void;
    handleVacationChange: (index: number, field: keyof VacationPeriod, value: string | number) => void;
}

export const useVacationCalculations = (): VacationCalculationResult => {
    const { watch, setValue } = useFormContext();
    const formData = watch();

    const { balance, expiredCompulsoryDays, nextCompulsoryPeriod, scheduledDays, vacationHistory } = useMemo(() => {
        const { startDate, vacationPeriods = [] } = formData;
        if (!startDate) {
            return {
                balance: 0,
                expiredCompulsoryDays: 0,
                nextCompulsoryPeriod: { days: 0, date: null },
                scheduledDays: 0,
                vacationHistory: []
            };
        }

        const todayStr = new Date().toISOString().slice(0, 10);
        const today = new Date(Date.UTC(parseInt(todayStr.substring(0, 4)), parseInt(todayStr.substring(5, 7)) - 1, parseInt(todayStr.substring(8, 10))));
        const [originalYear, originalMonth, originalDay] = startDate.split('-').map(Number);
        const admissionDate = new Date(Date.UTC(originalYear, originalMonth - 1, originalDay));
        const totalYearsOfWork = differenceInYears(today, admissionDate);
        const allValidPeriods = [...(vacationPeriods || [])].filter(p => p.startDate && p.endDate).sort((a, b) => a.startDate.localeCompare(b.startDate));
        const legacyPeriods = allValidPeriods.filter(period => isLegacyPeriod(period));

        const buckets: { creditDate: string; deadlineDate: string; balance: number; yearsToDeadline: number }[] = [];
        for (let year = 1; year <= totalYearsOfWork; year++) {
            const creditYear = originalYear + year;
            const creditDate = new Date(Date.UTC(creditYear, originalMonth - 1, originalDay));
            const deadlineDate = addUtcMonths(creditDate, SCHEDULE_LIMIT_MONTHS);
            const yearsToDeadline = (deadlineDate.getTime() - today.getTime()) / (1000 * 3600 * 24 * 365.25);
            buckets.push({
                creditDate: `${creditYear}-${String(originalMonth).padStart(2, '0')}-${String(originalDay).padStart(2, '0')}`,
                deadlineDate: deadlineDate.toISOString().slice(0, 10),
                balance: 30,
                yearsToDeadline: yearsToDeadline,
            });
        }

        for (const period of legacyPeriods) {
            let daysToDeduct = countVacationDays(period.startDate, period.endDate);
            for (const bucket of buckets) {
                if (daysToDeduct === 0) break;
                const deduction = Math.min(daysToDeduct, bucket.balance);
                bucket.balance -= deduction;
                daysToDeduct -= deduction;
            }
        }

        const usedDays = sumStartedDays(legacyPeriods, todayStr);
        const balance = (totalYearsOfWork * 30) - usedDays;
        const scheduledDays = sumScheduledDays(legacyPeriods, todayStr);
        const expiredCompulsoryDays = buckets.filter(b => b.deadlineDate < todayStr && b.balance > 0).reduce((acc, bucket) => acc + bucket.balance, 0);

        const nextBucketToExpire = buckets.filter(b => b.balance > 0 && b.deadlineDate > todayStr).sort((a, b) => a.deadlineDate.localeCompare(b.deadlineDate))[0];
        const nextCompulsoryPeriod: NextCompulsoryPeriod = nextBucketToExpire
            ? {
                  days: nextBucketToExpire.balance,
                  date: resolveEnjoymentStartDate(nextBucketToExpire.deadlineDate, 0) || null,
              }
            : { days: 0, date: null };

        const history: VacationHistoryEntry[] = [];
        buckets.forEach((bucket, index) => history.push({ date: bucket.creditDate, description: `Crédito Período Aquisitivo ${index + 1}`, credit: 30 }));
        allValidPeriods.forEach(period => {
            if (!isLegacyPeriod(period)) {
                history.push({
                    date: period.startDate,
                    description: 'Férias na solicitação — não debitado aqui',
                });
                return;
            }

            history.push({
                date: period.startDate,
                description: hasStarted(period, todayStr) ? 'Férias Gozadas' : 'Férias Programadas',
                debit: countVacationDays(period.startDate, period.endDate),
            });
        });
        history.sort((a, b) => a.date.localeCompare(b.date));

        return { balance, expiredCompulsoryDays, nextCompulsoryPeriod, scheduledDays, vacationHistory: history };
    }, [formData]);

    const handleAddVacation = useCallback(() => {
        const currentVacationPeriods = formData.vacationPeriods || [];
        const newVacation: VacationPeriod = {
            id: `vacation-${Date.now()}`,
            startDate: '',
            endDate: '',
            days: 0,
            observations: ''
        };
        setValue('vacationPeriods', [...currentVacationPeriods, newVacation]);
    }, [formData.vacationPeriods, setValue]);

    const handleRemoveVacation = useCallback((index: number) => {
        const currentVacationPeriods = formData.vacationPeriods || [];
        const updatedPeriods = currentVacationPeriods.filter((_: any, i: number) => i !== index);
        setValue('vacationPeriods', updatedPeriods);
    }, [formData.vacationPeriods, setValue]);

    const handleVacationChange = useCallback((index: number, field: keyof VacationPeriod, value: string | number) => {
        const currentVacationPeriods = [...(formData.vacationPeriods || [])];
        const period = { ...currentVacationPeriods[index] };

        if (field === 'days') {
            period.days = value === '' ? 0 : Number(value);
        } else {
            (period as any)[field] = value;
        }

        const days = period.days ?? 0;

        if (period.startDate && days > 0) {
            const [y, m, d] = period.startDate.split('-').map(Number);
            const endDate = addDays(new Date(Date.UTC(y, m - 1, d)), days - 1);
            period.endDate = endDate.toISOString().slice(0, 10);
        } else {
            period.endDate = '';
        }

        currentVacationPeriods[index] = period;
        setValue('vacationPeriods', currentVacationPeriods);
    }, [formData.vacationPeriods, setValue]);

    return {
        balance,
        expiredCompulsoryDays,
        nextCompulsoryPeriod,
        scheduledDays,
        vacationHistory,
        handleAddVacation,
        handleRemoveVacation,
        handleVacationChange,
    };
}; 